/**
 * Theme-level reusable anchors
 * - Link anywhere to `#fit-master` to scroll to the Year/Make/Model selector.
 * - Link anywhere to `#chat` to scroll to (and attempt to auto-open) the live chat widget.
 *
 * Notes for future reuse:
 * - Add more anchors by extending ANCHOR_DEFS below (selectors + optional onActivate).
 * - This script intentionally waits for dynamically-loaded widgets (MutationObserver).
 */

(() => {
  // ---- Config ---------------------------------------------------------------
  const DEFAULT_TIMEOUT_MS = 15000;
  const EXTRA_OFFSET_PX = 12; // Small breathing room below sticky header.
  const ACTIVATE_DEDUPE_MS = 900; // Prevent double-activations (click + hashchange/pageshow).

  /**
   * Define anchors here.
   * `targetSelectors` should be stable (prefer theme/app root containers, not ephemeral IDs).
   */
  const ANCHOR_DEFS = {
    // Client requirement: Year/Make/Model selector (PartFits / Fitmaster).
    'fit-master': {
      // Prefer stable app/root containers (avoid wizard IDs that can change per installation).
      targetSelectors: [
        // Fitmaster app block (Shopify app extension) commonly renders these:
        '.ymm-finder-section',
        '.ymm-finder-container',

        // PartFits/Part Finder widget containers:
        '#shopify-section-partfits-part-finder .gv-wizard-data',
        '#shopify-section-partfits-part-finder .gv-wizard-container',
        '#shopify-section-partfits-part-finder',
        '#section-part-finder .gv-wizard-data',
        '#section-part-finder .gv-wizard-container',
        '#section-part-finder',
        '.gv-partfinder .gv-wizard-data',
        '.gv-partfinder .gv-wizard-container',
        '.gv-partfinder',

        // Generic PartFits class names (last resort):
        '.gvwizard .gv-wizard-data',
        '.gvwizard .gv-wizard-container',
        '.gvwizard',
      ],
      // Optional: focus first dropdown after scroll to hint interactivity.
      onActivate: (targetEl) => {
        // On real mobile devices (coarse pointers), focusing a <select> can auto-open the picker
        // and may cause a scroll jump. Keep focus hinting for desktop only.
        try {
          if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
        } catch (_) {}

        const firstFocusable =
          targetEl?.querySelector?.(
            'input, button, [role="combobox"], a, [tabindex]:not([tabindex="-1"]), select'
          ) || null;
        if (firstFocusable && typeof firstFocusable.focus === 'function') {
          try {
            firstFocusable.focus({ preventScroll: true });
          } catch (_) {
            firstFocusable.focus();
          }
        }
      },
    },

    // Client requirement: live chat.
    chat: {
      // Many chat widgets (including Tidio) are fixed-position; scrolling isn't meaningful.
      // This anchor focuses on *opening* chat reliably.
      targetSelectors: [],
      onActivate: async () => {
        // Prefer Tidio "guaranteed open" (API + shadow DOM click), then fall back safely.
        const tidioOpened = await openTidioGuaranteed(DEFAULT_TIMEOUT_MS);
        if (!tidioOpened) await openChatBestEffort();
      },
    },
  };

  // ---- Utilities ------------------------------------------------------------
  const lastActivation = { name: null, at: 0 };

  function shouldDedupeActivation(name) {
    const now = Date.now();
    if (lastActivation.name === name && now - lastActivation.at < ACTIVATE_DEDUPE_MS) return true;
    lastActivation.name = name;
    lastActivation.at = now;
    return false;
  }

  function normalizeHash(hash) {
    if (!hash) return '';
    const str = String(hash).trim();
    // Accept "#chat", "/#chat", "https://domain/path#chat"
    const idx = str.indexOf('#');
    if (idx === -1) return '';
    return str.slice(idx + 1).trim();
  }

  function isVisible(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function firstMatch(selectors) {
    for (const sel of selectors) {
      try {
        const el = document.querySelector(sel);
        if (el && isVisible(el)) return el;
      } catch (_) {}
    }
    return null;
  }

  function readCssVarPx(varName) {
    try {
      const raw = getComputedStyle(document.documentElement).getPropertyValue(varName);
      const n = Number.parseFloat(String(raw).replace('px', '').trim());
      return Number.isFinite(n) ? n : 0;
    } catch (_) {
      return 0;
    }
  }

  function getStickyHeaderOffsetPx() {
    /**
     * Prefer the theme's own computed CSS vars:
     * - `--header-end-padded` is maintained by `StoreHeader.setHeaderEnd()` in `assets/main.js`
     *   and already accounts for desktop vs mobile padding.
     * This is more stable than querying specific header markup.
     */
    const cssHeaderEndPadded = readCssVarPx('--header-end-padded');
    if (cssHeaderEndPadded > 0) return cssHeaderEndPadded + EXTRA_OFFSET_PX;

    // Fallback (if vars aren't set yet for some reason): measure header-ish elements.
    const headerEl =
      document.querySelector('store-header.header') ||
      document.querySelector('header.header') ||
      document.querySelector('header') ||
      document.querySelector('.site-header') ||
      null;

    const announceEl =
      document.querySelector('.announcement-bar') ||
      document.querySelector('.shopify-section-announcement-bar') ||
      null;

    const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
    const announceH = announceEl ? announceEl.getBoundingClientRect().height : 0;

    return Math.max(0, headerH + announceH + EXTRA_OFFSET_PX);
  }

  function scrollToElementWithOffset(el) {
    const offset = getStickyHeaderOffsetPx();
    const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }

  function waitForElement(selectors, timeoutMs = DEFAULT_TIMEOUT_MS) {
    return new Promise((resolve) => {
      const immediate = firstMatch(selectors);
      if (immediate) return resolve(immediate);

      const startedAt = Date.now();
      const observer = new MutationObserver(() => {
        const found = firstMatch(selectors);
        if (found) {
          observer.disconnect();
          resolve(found);
        } else if (Date.now() - startedAt > timeoutMs) {
          observer.disconnect();
          resolve(null);
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });

      // Hard timeout fallback (in case no mutations fire).
      window.setTimeout(() => {
        try {
          observer.disconnect();
        } catch (_) {}
        resolve(firstMatch(selectors));
      }, timeoutMs);
    });
  }

  // ---- Chat open helpers ----------------------------------------------------
  let tidioOpenInFlight = null;
  let tidioLastUserActionAt = 0;

  // If the user manually interacts with the chat widget (especially close), never "fight" them by re-opening.
  // Note: events from shadow DOM are retargeted, but composedPath should include the host in modern browsers.
  document.addEventListener(
    'pointerdown',
    (evt) => {
      try {
        const path = typeof evt.composedPath === 'function' ? evt.composedPath() : [];
        const touchedTidio = path.some((n) => n && n.id === 'tidio-chat');
        if (touchedTidio) tidioLastUserActionAt = Date.now();
      } catch (_) {}
    },
    { capture: true }
  );

  function waitForCondition(checkFn, timeoutMs = DEFAULT_TIMEOUT_MS, intervalMs = 200) {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const tick = () => {
        let ok = false;
        try {
          ok = Boolean(checkFn());
        } catch (_) {
          ok = false;
        }
        if (ok) return resolve(true);
        if (Date.now() - startedAt > timeoutMs) return resolve(false);
        window.setTimeout(tick, intervalMs);
      };
      tick();
    });
  }

  async function waitForShadowElement(host, selectors, timeoutMs = DEFAULT_TIMEOUT_MS) {
    // Tidio renders inside an *open* shadow root on the #tidio-chat host.
    const startedAt = Date.now();
    const check = () => {
      try {
        const root = host && host.shadowRoot;
        if (!root) return null;
        for (const sel of selectors) {
          const el = root.querySelector(sel);
          if (el) return el;
        }
      } catch (_) {}
      return null;
    };

    const immediate = check();
    if (immediate) return immediate;

    return new Promise((resolve) => {
      const tick = () => {
        const found = check();
        if (found) return resolve(found);
        if (Date.now() - startedAt > timeoutMs) return resolve(null);
        window.setTimeout(tick, 150);
      };
      tick();
    });
  }

  async function openTidioGuaranteed(timeoutMs = 12000) {
    // Avoid double opens when multiple handlers fire close together.
    if (tidioOpenInFlight) return tidioOpenInFlight;

    tidioOpenInFlight = (async () => {
    const attemptStartedAt = Date.now();
    /**
     * This store uses Tidio (confirmed by rendered DOM: `#tidio-chat` host + shadow DOM).
     * We try the official API if available, otherwise we click the launcher inside the open shadow root.
     */
    const host = await waitForElement(['#tidio-chat'], timeoutMs);
    if (!host) return false;

    // Wait for shadow root to be attached (Tidio mounts UI asynchronously)
    const shadowReady = await waitForCondition(() => Boolean(host.shadowRoot), timeoutMs, 100);
    if (!shadowReady) return false;

    const getBtnWrap = () => {
      try {
        const root = host.shadowRoot;
        return root && (root.querySelector('[data-testid="widgetButton"]') || root.querySelector('#button'));
      } catch (_) {
        return null;
      }
    };

    let stateObserver = null;
    let everOpenedDuringAttempt = false;

    const isOpened = () => {
      const btnWrap = getBtnWrap();
      if (!btnWrap) return false;
      const cls = String(btnWrap.className || '');
      return /\bchat-opened\b/i.test(cls);
    };

    const isClosed = () => {
      const btnWrap = getBtnWrap();
      if (!btnWrap) return false;
      const cls = String(btnWrap.className || '');
      return /\bchat-closed\b/i.test(cls);
    };

    const waitOpened = (ms) => waitForCondition(() => isOpened(), ms, 120);

    // Wait for the actual clickable bubble button to exist (avoid clicking labels/toggles twice).
    const bubbleBtn =
      (await waitForShadowElement(host, ['[data-testid="widgetButtonBody"]', '#button-body'], timeoutMs)) || null;

    // If already open, do nothing (prevents accidental toggling closed).
    if (isOpened()) return true;

    // Observe open/close state transitions so we can respect quick manual closes
    // (close events inside iframes won't bubble, but the outer button state typically updates).
    try {
      const btnWrap = getBtnWrap();
      if (btnWrap) {
        everOpenedDuringAttempt = isOpened();
        stateObserver = new MutationObserver(() => {
          try {
            if (isOpened()) everOpenedDuringAttempt = true;
            // If chat was opened during this anchor attempt and then becomes closed,
            // assume user closed it and never re-open automatically.
            if (everOpenedDuringAttempt && isClosed()) {
              tidioLastUserActionAt = Date.now();
            }
          } catch (_) {}
        });
        stateObserver.observe(btnWrap, { attributes: true, attributeFilter: ['class'] });
      }
    } catch (_) {}

    try {
      // Prefer API open (if available) BUT only do one strategy; verify opened before falling back.
      const api = window.tidioChatApi;
      if (api && typeof api.open === 'function') {
        try {
          api.open();
        } catch (_) {}
        if (await waitOpened(2500)) return true;
      }

      // If the user interacted with chat during this attempt (e.g. quickly closed), do not reopen.
      if (tidioLastUserActionAt > attemptStartedAt) return true;

      // Fallback: click the bubble ONLY if it looks closed.
      if (bubbleBtn && isClosed()) {
        try {
          bubbleBtn.click();
        } catch (_) {}

        // If it opened, great. If we can't detect open reliably, still return true to avoid
        // any other "best-effort" opener from toggling it again (which can auto-close).
        if (await waitOpened(2500)) return true;
        return true;
      }

      return isOpened();
    } finally {
      try {
        if (stateObserver) stateObserver.disconnect();
      } catch (_) {}
    }
    })();

    try {
      return await tidioOpenInFlight;
    } finally {
      tidioOpenInFlight = null;
    }
  }

  async function openChatBestEffort() {
    // 1) Provider APIs (safe no-ops if not present)
    try {
      // Crisp
      if (window.$crisp && Array.isArray(window.$crisp)) {
        window.$crisp.push(['do', 'chat:open']);
        return;
      }
      // Intercom
      if (typeof window.Intercom === 'function') {
        window.Intercom('show');
        return;
      }
      // Zendesk
      if (typeof window.zE === 'function') {
        window.zE('messenger', 'open');
        return;
      }
      // Tidio
      if (window.tidioChatApi && typeof window.tidioChatApi.open === 'function') {
        window.tidioChatApi.open();
        return;
      }
      // Gorgias (common pattern; harmless if unavailable)
      if (window.GorgiasChat && typeof window.GorgiasChat.open === 'function') {
        window.GorgiasChat.open();
        return;
      }
    } catch (_) {
      // Continue to click fallback
    }

    // 2) Clickable launcher fallback
    const launcherSelectors = [
      // Shopify Inbox / generic
      'button[aria-label*="chat" i]',
      'button[title*="chat" i]',
      '[role="button"][aria-label*="chat" i]',
      // Generic "launcher" patterns
      'button[id*="chat" i]',
      'button[class*="chat" i]',
      '[class*="chat" i][role="button"]',
      '[data-testid*="chat" i]',
    ];

    const launcher = await waitForElement(launcherSelectors, 8000);
    if (launcher) {
      try {
        launcher.click();
      } catch (_) {}
    }
  }

  // ---- Main ----------------------------------------------------------------
  async function activateAnchor(anchorName) {
    const def = ANCHOR_DEFS[anchorName];
    if (!def) return;
    if (shouldDedupeActivation(anchorName)) return;

    // If selectors exist, wait for element and scroll to it.
    if (Array.isArray(def.targetSelectors) && def.targetSelectors.length > 0) {
      const targetEl = await waitForElement(def.targetSelectors);
      if (targetEl) {
        scrollToElementWithOffset(targetEl);
        // A second measurement shortly after in case sticky header collapses/expands.
        window.setTimeout(() => {
          try {
            scrollToElementWithOffset(targetEl);
          } catch (_) {}
        }, 250);

        if (typeof def.onActivate === 'function') def.onActivate(targetEl);
        return;
      }
    }

    // No element to scroll to (or not found) – still run onActivate if provided (e.g. chat).
    if (typeof def.onActivate === 'function') await def.onActivate(null);
  }

  function shouldHandleHash(hashName) {
    return Boolean(hashName && Object.prototype.hasOwnProperty.call(ANCHOR_DEFS, hashName));
  }

  function handleCurrentHash() {
    const name = normalizeHash(window.location.hash);
    if (shouldHandleHash(name)) {
      activateAnchor(name);
    }
  }

  // Handle direct visits with #hash
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleCurrentHash);
  } else {
    handleCurrentHash();
  }

  // Handle back/forward cache restores (mobile Safari/Chrome can skip DOMContentLoaded)
  window.addEventListener('pageshow', handleCurrentHash);

  // Handle in-page hash changes (including history navigation)
  window.addEventListener('hashchange', handleCurrentHash);

  // Intercept clicks for these anchors so we can wait for late-loading widgets.
  document.addEventListener(
    'click',
    (evt) => {
      const a = evt.target && evt.target.closest ? evt.target.closest('a[href]') : null;
      if (!a) return;

      const href = a.getAttribute('href') || '';
      const name = normalizeHash(a.hash || href);
      if (!shouldHandleHash(name)) return;

      // Only prevent navigation if this link targets the current document.
      // If it points to another page (e.g. "/collections/x#chat"), let the browser navigate;
      // our on-load handler will run on the destination page.
      let sameDoc = false;
      try {
        const url = new URL(a.href, window.location.href);
        // Treat same-path hash links as "same document" even if the querystring differs.
        // This is important for Shopify theme preview URLs (they include `?preview_theme_id=...`),
        // where menus/links may use "/#chat" (no query). We must not navigate away and drop preview params.
        sameDoc =
          url.origin === window.location.origin &&
          url.pathname === window.location.pathname &&
          (url.search === window.location.search || url.search === '');
      } catch (_) {
        sameDoc = href.startsWith('#');
      }

      if (sameDoc) {
        evt.preventDefault();
        try {
          history.pushState(null, '', `#${name}`);
        } catch (_) {}
        activateAnchor(name);
      }
    },
    { capture: true }
  );
})();

